import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogoutOutlined, BulbOutlined } from '@ant-design/icons';
import { useAuthStore, useAgentStore } from '../stores';
import { useTheme } from '../context/ThemeContext';
import { logout } from '../lib/api';
import { useDismissibleLayer } from '../hooks/useDismiss';
import { runUnsavedGuards } from '../hooks/useUnsavedChangesGuard';
import { routes } from '../lib/routes';
import { UserBadge } from './UserBadge';
import { UserAvatar } from './UserAvatar';

interface Props {
  compact?: boolean;
  onLogout?: () => void;
  onLogoutStart?: () => void;
  onLogoutFailure?: () => void;
}

export default function UserPanel({ compact = false, onLogout, onLogoutStart, onLogoutFailure }: Props) {
  const currentUser = useAuthStore(s => s.currentUser);
  const agentCount = useAgentStore(s => s.agents.length);
  const agentsUnknown = useAgentStore(s => s.loadFailed && s.agents.length === 0);
  const loadAgents = useAgentStore(s => s.loadAgents);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isAgents = location.pathname.startsWith('/agents');
  const [showAccountPanel, setShowAccountPanel] = useState(false);
  const [logoutError, setLogoutError] = useState('');
  const accountPanelRef = useRef<HTMLDivElement>(null);

  const closeAccountPanel = useCallback(() => setShowAccountPanel(false), []);
  useDismissibleLayer({
    active: showAccountPanel,
    within: accountPanelRef,
    onEscape: closeAccountPanel,
    onOutsidePress: closeAccountPanel,
  });

  useEffect(() => {
    if (!compact) loadAgents();
  }, [compact, loadAgents]);

  const handleLogout = async () => {
    setLogoutError('');
    onLogoutStart?.();
    try {
      await logout();
      onLogout?.();
    } catch {
      onLogoutFailure?.();
      setLogoutError('Could not sign out. Try again.');
    }
  };

  if (!currentUser) return null;

  return (
    <div className={`user-panel${compact ? ' user-panel-compact' : ''}`} data-testid="sidebar-footer-primary">
      <div className="user-panel-inner" data-testid="sidebar-footer-primary-actions">
        <div className="sidebar-account" ref={accountPanelRef}>
          {compact ? (
            <button
              type="button"
              className="sidebar-account-trigger"
              aria-label="Profile and settings"
              title="Profile and settings"
              aria-expanded={showAccountPanel}
              aria-controls={showAccountPanel ? 'sidebar-account-panel' : undefined}
              onClick={() => setShowAccountPanel((open) => !open)}
              data-testid="sidebar-account-trigger"
            >
              <UserAvatar
                userId={currentUser.id}
                displayName={currentUser.display_name}
                avatarUrl={currentUser.avatar_url}
                size={32}
                role={currentUser.role}
              />
            </button>
          ) : (
            <UserBadge
              userId={currentUser.id}
              displayName={currentUser.display_name}
              role={currentUser.role}
              avatarUrl={currentUser.avatar_url}
              size={28}
              onAvatarClick={() => setShowAccountPanel((open) => !open)}
              data-testid="sidebar-account-trigger"
            />
          )}
          {showAccountPanel && (
            <div
              id="sidebar-account-panel"
              className="sidebar-account-panel"
              data-testid="sidebar-account-panel"
              role="dialog"
              aria-label="Account"
            >
              <div className="sidebar-account-panel-title">Account</div>
              <div className="sidebar-account-summary">
                <UserAvatar userId={currentUser.id} displayName={currentUser.display_name} size={32} />
                <div className="sidebar-account-summary-text">
                  <div className="sidebar-account-name" data-testid="sidebar-account-name">
                    {currentUser.display_name}
                  </div>
                  <div className="sidebar-account-role" data-testid="sidebar-account-role">
                    {currentUser.role}
                  </div>
                </div>
              </div>
              <div className="sidebar-account-divider" />
              <button
                type="button"
                className="sidebar-footer-secondary-item"
                onClick={() => {
                  toggleTheme();
                }}
              >
                <BulbOutlined />
                <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
              </button>
              {onLogout && (
                <button
                  type="button"
                  className="sidebar-footer-secondary-item logout-btn"
                  title="Logout"
                  data-testid="sidebar-account-logout"
                  onClick={() => {
                    setShowAccountPanel(false);
                    void handleLogout();
                  }}
                >
                  <LogoutOutlined />
                  <span>Logout</span>
                </button>
              )}
            </div>
          )}
        </div>
        {!compact && currentUser.role !== 'agent' && (
          <span
            className={`user-panel-agents-link${isAgents ? ' user-panel-agents-link-active' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => {
              if (!runUnsavedGuards()) return;
              navigate(routes.agents());
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                if (!runUnsavedGuards()) return;
                navigate(routes.agents());
              }
            }}
          >
            {agentCount > 0 ? (
              <>
                with <strong>{agentCount}</strong> {agentCount === 1 ? 'agent' : 'agents'}
              </>
            ) : agentsUnknown ? (
              // A list we could not load is not a list of none.
              <>your agents</>
            ) : (
              <>+ create agent</>
            )}
          </span>
        )}
      </div>
      {logoutError && (
        <div className="login-error" role="alert">
          {logoutError}
        </div>
      )}
    </div>
  );
}
